import React from 'react'
import { useI18n } from '../../i18n'
import { normalizeAssetType, assetTypeI18nKey, isScannableAssetType } from '../../constants/assetTaxonomy'
import type { AssetOperationalSummaryRow } from '../../services/assetOperationalApi'
import type { DrillDownTarget } from './operationalDrillDownContent'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import {
  SectionCell,
  ChipGroup,
  TopVulnerabilitiesList,
  TopFailedControlsList,
  TopOpenRisksList,
} from './systemPostureRowParts'

interface Props {
  row: AssetOperationalSummaryRow
  selected?: boolean
  onSelect: () => void
  onOpenPassport: () => void
  onOpenGraph: () => void
  onOpenScans: () => void
  onOpenRelationships: () => void
  onDrillDown: (target: DrillDownTarget) => void
  onEdit?: () => void
  onScan?: () => void
}

export default function AssetOperationalRow({
  row,
  selected,
  onSelect,
  onOpenPassport,
  onOpenGraph,
  onOpenScans,
  onOpenRelationships,
  onDrillDown,
  onEdit,
  onScan,
}: Props) {
  const { t } = useI18n()
  const assetId = String(row.assetId)
  const typeLabel = t(assetTypeI18nKey(normalizeAssetType(row.type)), row.type)
  const scannable = isScannableAssetType(row.type)

  return (
    <article className={`${styles.systemPostureRow} ${selected ? styles.systemPostureRowSelected : ''}`.trim()}>
      <div
        className={styles.rowInner}
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
          }
        }}
        aria-selected={selected}
        aria-label={row.name}
      >
        <div className={styles.rowGrid}>
          <SectionCell label={t('assets.assetPassport.asset', 'Asset')} onActivate={onSelect}>
            <div>
              <strong className={styles.systemName}>{row.name}</strong>
              {(row as { networkTarget?: string }).networkTarget ? (
                <div className={styles.systemMeta}>
                  <span>{(row as { networkTarget?: string }).networkTarget}</span>
                </div>
              ) : null}
              <div className={styles.systemMeta}>
                <span>{typeLabel}</span>
                {row.environment ? <span> · {row.environment}</span> : null}
                {row.criticality ? <span> · {row.criticality}</span> : null}
              </div>
            </div>
          </SectionCell>
          <SectionCell label={t('assets.assetPassport.dataAwareness', 'Data')} onActivate={onOpenRelationships}>
            <ChipGroup
              items={(row.dataCategories || []).slice(0, 4)}
              emptyLabel={t('assets.assetPassport.noDataCategories', 'No data categories')}
              variant="violet"
              onChipClick={onOpenRelationships}
            />
          </SectionCell>
          <SectionCell
            label={t('assets.assetPassport.vulnerabilities', 'Vulnerabilities')}
            onActivate={() => onDrillDown({ kind: 'vulnerability', systemId: assetId })}
          >
            <TopVulnerabilitiesList
              items={(row.topVulnerabilities || []).slice(0, 3)}
              empty={t('assets.postureRow.noVulnerabilities', 'No critical vulnerabilities')}
              onDrillDown={(target) => onDrillDown({ ...target, systemId: assetId })}
              systemId={assetId}
            />
            <span className="text-xs text-gray-500 block mt-1">
              {row.vulnerabilityCount} {t('assets.assetPassport.total', 'total')}
              {row.criticalVulnerabilityCount > 0
                ? ` · ${row.criticalVulnerabilityCount} ${t('assets.assetPassport.criticalShort', 'crit/high')}`
                : ''}
            </span>
          </SectionCell>
          <SectionCell
            label={t('assets.assetPassport.controls', 'Controls')}
            onActivate={() => onDrillDown({ kind: 'control', systemId: assetId })}
          >
            <TopFailedControlsList
              items={(row.topFailedControls || []).slice(0, 2)}
              empty={t('assets.postureRow.noFailedControls', 'No failed controls')}
              onDrillDown={(target) => onDrillDown({ ...target, systemId: assetId })}
              systemId={assetId}
            />
          </SectionCell>
          <SectionCell
            label={t('assets.assetPassport.risks', 'Risks')}
            onActivate={() => onDrillDown({ kind: 'risk', systemId: assetId, sourceType: 'asset', sourceId: assetId })}
          >
            <TopOpenRisksList
              items={(row.topOpenRisks || []).slice(0, 2)}
              empty={t('assets.postureRow.noOpenRisks', 'No open risks')}
              onDrillDown={(target) => onDrillDown({ ...target, systemId: assetId })}
              systemId={assetId}
            />
          </SectionCell>
          <SectionCell label={t('assets.assetPassport.actions', 'Actions')}>
            <div className={styles.rowQuickActions}>
              <button type="button" className={styles.rowActionBtn} onClick={(e) => { e.stopPropagation(); onOpenPassport() }}>
                {t('assets.assetPassport.passport', 'Passport')}
              </button>
              <button type="button" className={styles.rowActionBtn} onClick={(e) => { e.stopPropagation(); onOpenGraph() }}>
                {t('assets.assetPassport.graph', 'Graph')}
              </button>
              {scannable && onScan ? (
                <button type="button" className={styles.rowActionBtn} onClick={(e) => { e.stopPropagation(); onScan() }}>
                  {t('assets.inventory.scan', 'Scan')}
                </button>
              ) : null}
              {onEdit ? (
                <button type="button" className={styles.rowActionBtn} onClick={(e) => { e.stopPropagation(); onEdit() }}>
                  {t('common.edit', 'Edit')}
                </button>
              ) : null}
            </div>
          </SectionCell>
        </div>
      </div>
    </article>
  )
}

